<template>
  <form
    :class="bem()"
    novalidate
    @submit.prevent="handleSubmit"
  >
    <fieldset>
      <VisionTextInput
        id="name"
        v-model:value="name"
        label="Name"
        name="name"
        placeholder="Your name"
        label-hint="required"
        :error="errors.name"
        :class="bem('input')"
      />
      <VisionTextInput
        id="email"
        v-model:value="email"
        label="Email"
        name="email"
        placeholder="Email"
        label-hint="required"
        :error="errors.email"
        :class="bem('input')"
      />
      <VisionCheckbox
        id="mailingAgree"
        v-model="mailingAgree"
        input-value="mailingAgree"
        name="mailingAgree"
        :error="errors.mailingAgree"
        :label-text="content.mailingAgreementText"
        :class="bem('checkbox')"
      />
      <VisionCheckbox
        id="termsAgree"
        v-model="termsAgree"
        input-value="termsAgree"
        name="termsAgree"
        :error="errors.termsAgree"
        :label-text="content.termsAgreementText"
        :class="bem('checkbox')"
      />
    </fieldset>
    <VisionButton
      type="submit"
      background-colour="black"
      :square="true"
      :disabled="!allRequiredFieldsFilled || submitting"
      :class="bem('button')"
    >
      <template #content>
        <span v-if="!submitting"> {{ content.formButtonText }} </span>
        <span v-else> Please wait... </span>
      </template>
    </VisionButton>
    <div :class="bem('form-msg')">
      {{ formErrorMessage }}
    </div>
  </form>
</template>

<script setup>
const bem = useBEM('StravaForm')
const route = useRoute()
const router = useRouter()
const { $sentry, $track } = useNuxtApp()
const userStore = useUserStore()
const { public: { competitions: { apiEndpoint } } } = useRuntimeConfig()

const props = defineProps({
  content: { type: Object, default: () => {} },
})

const name = ref('')
const email = ref('')
const termsAgree = ref(false)
const mailingAgree = ref(false)
const submitting = ref(false)
const allRequiredFieldsFilled = ref(false)
const errors = ref({
  name: '',
  email: '',
})
const formErrorMessage = ref('')
const totalErrorCount = ref(0)

function validate() {
  if (!name.value) {
    errors.value.name = 'Please enter your name'
  }

  if (!email.value) {
    errors.value.email = 'Please enter your email'
  }

  if (!mailingAgree.value) {
    errors.value.mailingAgree = true
  }

  if (!termsAgree.value) {
    errors.value.termsAgree = true
  }

  totalErrorCount.value = Object.values(errors.value).filter(
    (value) => !!value
  ).length
}

async function submitForm() {
  return $fetch(`${apiEndpoint}/submit/`, {
    method: 'POST',
    body: {
      first_name: name.value,
      email: email.value,
      referrer: null,
      join: true,
      comp_id: props.content.competition.competitionId,
      slug: props.content.competition.slug,
      host: `${window.location.origin}${route.path}`,
      metadata: {
        custLocation: route.params.locale,
        slug: props.content.competition.slug,
      },
    },
  })
}

async function handleSubmit() {
  submitting.value = true

  validate()

  if (totalErrorCount.value > 0) {
    submitting.value = false
    return
  }

  let submitResponse

  try {
    submitResponse = await submitForm()

    $track.competition.formSubmitted({ slug: props.content.competition.slug })
  } catch (submitError) {
    submitting.value = false
    handleErrors(submitError)
    $sentry.captureException(submitError)
    return
  }

  try {
    userStore.setUserEmail(email.value);
    $track.updateUserData();
  } catch(e) {
    $sentry.captureException(e)
  }

  if (submitResponse.verification_needed) {
    router.push({
      query: {
        verificationRequired: true,
        code: submitResponse.code,
        ...route.query
      },
    })
    return
  } else if (submitResponse.verification_needed === false) {
    if (submitResponse.portal_link) {
      const responseParams = new URL(submitResponse.portal_link).searchParams

      router.replace({
        query: {
          mode: responseParams.get('mode'),
          key: responseParams.get('key'),
        },
      })
    } else {
      formErrorMessage.value =
        'Unable to generate a discount code at this time.'
      throw createError({ message: formErrorMessage.value })
    }
  }

  submitting.value = false
}

function handleErrors(error) {
  const responseCode = error.status
  const responseData = error.data?.[0]

  if (responseCode === 429 || responseCode === 500)
    formErrorMessage.value =
      'Sorry, an error has occurred. Please try again later.'

  if (responseCode === 400) {
    switch (responseData) {
      case 'ALREADY_ENTERED_EMAIL':
        formErrorMessage.value = 'You have already entered.'
        break
      case 'ALREADY_ENTERED_IP':
        formErrorMessage.value = 'You have already entered.'
        break
      case 'INVALID_EMAIL_ADDRESS':
        formErrorMessage.value =
          'Invalid email address, please check and try again'
        break
      case 'DISPOSABLE_EMAIL_ADDRESS':
        formErrorMessage.value =
          'Invalid email address, please check and try again'
        break
      case 'MISSING_EMAIL':
        formErrorMessage.value =
          'Missing email address, please check and try again'
        break
      case 'MISSING_FIRST_NAME':
        formErrorMessage.value =
          'Missing first name, please check and try again'
        break
      case 'UNKNOWN_SLUG':
        formErrorMessage.value = 'Unable to get competitions details.'
        break
      default:
        formErrorMessage.value =
          'Sorry, an error has occurred. Please try again later.'
    }
  }
}

function checkAllRequiredFieldsFilled(formValues) {
  allRequiredFieldsFilled.value = formValues.filter(
    (formValue) => !formValue
  ).length === 0
}

onMounted(() => {
  checkAllRequiredFieldsFilled([
    name.value,
    email.value,
    termsAgree.value,
    mailingAgree.value,
    submitting.value
  ])

  $track.competition.viewed({ slug: props.content.competition.slug })
})

watch(name, () => {
  errors.value.name = ''
  formErrorMessage.value = ''
})

watch(email, () => {
  errors.value.email = ''
  formErrorMessage.value = ''
})

watch(mailingAgree, () => {
  errors.value.mailingAgree = false
})

watch(termsAgree, () => {
  errors.value.termsAgree = false
})

watch([name, email, mailingAgree, termsAgree], (newFormValues) => {
  checkAllRequiredFieldsFilled(newFormValues)
})
</script>

<style lang="postcss" setup>
.StravaForm__input:not(:last-child) {
  @apply mb-4;
}

.StravaForm__checkbox {
  @apply mb-2;
}

.StravaForm__button {
  @apply w-full mt-4;
}

.StravaForm__form-msg {
  @apply text-red mt-3;
}
</style>
